import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { ContentfulQueryKeys, useContentfulQuery } from '@oresundsbron/api';
import {
  Accordion,
  Box,
  Grid,
  PageWrapper,
  Typography,
  Button,
} from '@oresundsbron/bridge-ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';
import { RichText } from '../../components/RichText';
import { SEOMetadata } from '../../components/SEOMetadata';
import { Breadcrumbs } from '../Breadcrumbs';
import { DefaultLink } from '~/components/Links/DefaultLink';
import { useFAQQuestion } from '~/hooks/useFAQ';
import { FAQPage, WithContext } from 'schema-dts';
import { useLinksV2 } from '~/hooks';

const col = cx(
  'col-span-full',
  'sm:col-span-6',
  'sm:col-start-2',
  'md:col-span-8',
  'md:col-start-3'
);

export const FAQQuestion: FC<{ id: string }> = ({ id }) => {
  const { locale, isPreview, asPath } = useRouter();
  const { findPageId, getLink } = useLinksV2();
  const { t } = useTranslation(['common'], { nsMode: 'fallback' });

  const idSplit = id.includes('_') ? id.split('_')[0] : id;

  const { seoMetadata, data } = useFAQQuestion({ id: idSplit });

  const groupId = useMemo(() => {
    const link = getLink(id);
    if (!link) return '';
    const slugs = link.slugs.slice(0, -1);
    const path = '/' + slugs.join('/');
    return findPageId(path) || '';
  }, [findPageId, id, getLink]);

  const { data: group } = useContentfulQuery(
    ContentfulQueryKeys.FAQGroup({ id: groupId, locale, preview: isPreview }),
    (q) => q.FAQ.getFaqGroup(locale || '', groupId, isPreview)
  );

  const predefinedJsonLd: WithContext<FAQPage> = useMemo(() => {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: data?.question || '',
          acceptedAnswer: {
            '@type': 'Answer',
            text: data?.shortAnswer?.json
              ? documentToPlainTextString(data.shortAnswer.json)
              : '',
          },
        },
      ],
    };
  }, [data]);

  const content = data?.answer ? (
    <Box className={col}>
      <RichText content={data.answer} />
    </Box>
  ) : undefined;

  const groupItems = useMemo(() => {
    if (!group?.questions) return undefined;
    const others = group.questions.filter((q) => q.slug !== id);
    return others.length > 0 ? others : undefined;
  }, [group, id]);

  const createFAQLink = (
    asPath: string,
    groupSlug: string,
    questionSlug: string
  ): string => {
    const parts = asPath.split('/').filter(Boolean);
    const index = parts.indexOf(groupSlug);
    const base = '/' + parts.slice(0, index).join('/');
    return `${base}/${groupSlug}/${questionSlug}`;
  };

  return (
    <>
      <SEOMetadata
        items={seoMetadata}
        predefinedJsonLd={predefinedJsonLd}
        customJsonLd={data?.jsonLd}
      />
      <PageWrapper>
        <Grid className="col-start-2 my-4 gap-y-8 md:my-12 md:gap-y-12 ">
          <Breadcrumbs className="col-span-full" id={id} />
          {content && data ? (
            <>
              <Typography as="h1" intent="display" className={col}>
                {data.question}
              </Typography>
              {content}
              {groupItems ? (
                <>
                  <hr className={cx(col, 'border-neutral-500')} />
                  <Typography as="h2" intent="headline" className={col}>
                    {group?.title}
                  </Typography>
                  <Accordion.Root
                    type="single"
                    collapsible
                    className={cx(
                      'grid',
                      'grid-cols-1',
                      'sm:grid-cols-2',
                      'gap-4',
                      col
                    )}
                  >
                    {groupItems.map((q) => (
                      <Accordion.Item
                        key={q.sys.id}
                        value={q.sys.id}
                        label={q.question}
                      >
                        <Box className="flex flex-col">
                          <RichText content={q.shortAnswer} />
                          <Button
                            href={createFAQLink(
                              asPath,
                              group?.slug || '',
                              q.slug
                            )}
                            as={DefaultLink}
                            intent="text"
                            className="ml-auto"
                          >
                            {t('action.readMore')}
                          </Button>
                        </Box>
                      </Accordion.Item>
                    ))}
                  </Accordion.Root>
                </>
              ) : null}
            </>
          ) : null}
        </Grid>
      </PageWrapper>
    </>
  );
};
